import { useState, useEffect } from "react";
import { useOutletContext, useParams, useLocation } from "react-router-dom";
import VoiceAssistant from "./Assistant/VoiceAssistant";
import { SmsAssistant } from "./Assistant/SmsAssistant";
import WhatsappAssistant from "./Assistant/WhatsappAssistant";
import { ChatAssistant } from "./Assistant/ChatAssistant";
import { EmailAssistant } from "./Assistant/EmailAssistant";
import { getAssistant } from "./service";
import { MessengerAssistant } from "./Assistant/MessengerAssistant";
import { AssistantDuplicate } from "./AssistantDuplicate";
import { useNavigate } from "react-router-dom";
import { InstagramAssistant } from "./Assistant/InstagramAssistant";
import { UserAccessLevel } from "V2.0/models/UserProfile";
import useRoleAccessBasedAssistantSections from "./Assistant/common/useRoleAccessBasedAssistantSections";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

export enum ASSISTANT {
  VOICE = "voice",
  EMAIL = "email",
  SMS = "sms",
  WHATSAPP = "whatsapp",
  CHAT = "chat",
  MESSENGER = "messenger",
  INSTAGRAM = "instagram",
}

const AssistantComponent = {
  [ASSISTANT.VOICE]: (props) => <VoiceAssistant {...props} />,
  [ASSISTANT.SMS]: (props) => <SmsAssistant {...props} />,
  [ASSISTANT.WHATSAPP]: (props) => <WhatsappAssistant {...props} />,
  [ASSISTANT.EMAIL]: (props) => <EmailAssistant {...props} />,
  [ASSISTANT.CHAT]: (props) => <ChatAssistant {...props} />,
  [ASSISTANT.MESSENGER]: (props) => <MessengerAssistant {...props} />,
  [ASSISTANT.INSTAGRAM]: (props) => <InstagramAssistant {...props} />,
};

function flatAssistant(assistant) {
  const { assistantSettings, workspace, ...restAssistant } = assistant;

  const { assistantTools, modelSettings, ...restSettings } = assistantSettings;

  const { _id, ...flatAssistant } = {
    assistantId: assistant._id,
    workspaceId: workspace,
    ...restAssistant,
    modelSettings,
    ...assistantTools,
    ...restSettings,
    ...(assistantSettings?.ttsSettings
      ? { ttsSettings: assistantSettings.ttsSettings }
      : {}),
    ...(assistantSettings?.transcriptionSettings
      ? { transcriptionSettings: assistantSettings.transcriptionSettings }
      : {}),
    ...(assistantSettings?.voiceSettings
      ? { voiceSettings: assistantSettings.voiceSettings }
      : {}),
  };

  return flatAssistant;
}

export function useAssistant(assistantId, workspaceId) {
  const [assistant, setAssistant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const syncAssistant = (data) => setAssistant(data);
  const syncFlatAssistant = (data) => setAssistant(flatAssistant(data));

  const fetchAssistant = async () => {
    setIsLoading(true);
    try {
      const response = await getAssistant({
        workspaceId,
        assistantId,
      });
      if (response.status === 200) {
        syncFlatAssistant(response.data);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
      console.error("Error getting assistant");
    }
  };

  return {
    assistant,
    isLoading,
    isError,
    syncAssistant,
    syncFlatAssistant,
    fetchAssistant,
  };
}

function getVisibilityConfig(assistant) {
  return {
    showPrompt: !(assistant?.modelSettings?.promptSettings?.hidePrompt ?? false),
    showAssistantCoach: assistant?.visibilitySettings?.showAssistantCoach,
    showAssistantCheckpointing: assistant?.visibilitySettings?.showAssistantCheckpointing
  }
}

export function Assistant() {
  const {
    projectId,
    company,
    isViewOnly,
    user,
    thirdpartyData,
    isThirdPartyLoading,
  } = useOutletContext<{
    projectId: string;
    company: object;
    isViewOnly: boolean;
    user: {
      userAccessLevel: UserAccessLevel;
      applicationSectionsVisible: Array<string>;
      assistantSectionsVisible: Array<string>;
    };
    thirdpartyData: any;
    isThirdPartyLoading: boolean;
  }>();
  const { assistantId } = useParams();
  const { assistant, syncAssistant, syncFlatAssistant, fetchAssistant, isLoading } =
    useAssistant(assistantId, projectId);
  const [openDuplicate, setOpenDuplicate] = useState(false);
  const navigate = useNavigate();

  const visibilityConfig = getVisibilityConfig(assistant);
  const roleAccessBasedAssistantSections = useRoleAccessBasedAssistantSections(user, assistant?.assistantType, visibilityConfig);

  useEffect(() => {
    if (assistantId && projectId) {
      fetchAssistant();
    }
  }, [assistantId, projectId]);

    if (isLoading) {
      return (
        <div className="h-full w-full flex items-center justify-center">
          <AiOutlineLoading3Quarters className="animate-spin size-8" />
        </div>
      );
    }

  return (
    assistant && (
      <>
        {AssistantComponent[assistant.assistantType]({
          ...assistant,
          syncAssistant,
          syncFlatAssistant,
          onDuplicate: () => setOpenDuplicate(true),
          company,
          locked: assistant?.locked || isViewOnly,
          sections: roleAccessBasedAssistantSections.sections,
          configurationSections: roleAccessBasedAssistantSections.configurationSections,
          fetchAssistant,
          thirdpartyData,
          isThirdPartyLoading,
        })}
        {openDuplicate && (
          <AssistantDuplicate
            isOpen={openDuplicate}
            onClose={() => setOpenDuplicate(false)}
            assistantId={assistantId}
            assistantName={assistant.name}
            workspaceId={projectId}
            onSuccess={(assistantId) => {
              navigate(`/assistants/${assistantId}`);
            }}
          />
        )}
      </>
    )
  );
}
